import React, { useRef, useState } from "react";
import api from "../api";
import CloneAvatar from "./CloneAvatar";
import { Button } from "./ui/button";
import { Camera, Sparkle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import useLang from "../hooks/useLang";

export default function AvatarUploader({ clone, onChange }) {
  const { t } = useLang();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un'immagine");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/clones/${clone.id}/avatar`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      onChange?.(data);
      toast.success(t("avatarGenerated"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!clone.avatar_image_path) return;
    try {
      const { data } = await api.delete(`/clones/${clone.id}/avatar`);
      onChange?.(data);
      toast.success(t("removeAvatar"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
    }
  };

  return (
    <div className="border border-zinc-200 rounded-md p-5 flex items-center gap-5" data-testid="avatar-uploader">
      <CloneAvatar clone={clone} size={72} />
      <div className="flex-1">
        <div className="overline mb-1">/// Avatar cartoon</div>
        <p className="text-xs text-zinc-500 mb-3 leading-relaxed">{t("uploadPhotoDesc")}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={pick}
            disabled={uploading}
            data-testid="avatar-upload-btn"
            className="bg-zinc-950 hover:bg-zinc-800 rounded-md gap-2 h-9 text-xs px-3"
          >
            {uploading ? (
              <>
                <Sparkle size={14} weight="fill" className="animate-pulse" /> {t("generatingAvatar")}
              </>
            ) : (
              <>
                <Camera size={14} weight="fill" /> {clone.avatar_image_path ? t("changePhoto") : t("uploadPhoto")}
              </>
            )}
          </Button>
          {clone.avatar_image_path && (
            <Button
              type="button"
              variant="ghost"
              onClick={remove}
              data-testid="avatar-remove-btn"
              className="text-red-600 hover:text-red-700 gap-1.5 h-9 text-xs"
            >
              <Trash size={14} /> {t("removeAvatar")}
            </Button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleFile}
          className="hidden"
          data-testid="avatar-file-input"
        />
      </div>
    </div>
  );
}
